import { Button, TextField, Tooltip } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import {
	deletePartidaCategory,
	getPartidaCategoryById,
	getPartidaCategories,
} from "../../../../api/partidaCategory";
import { ROLES } from "../../../../utils/constants";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import CreatePartidaCategoryDialog from "../../../components/dialogs/CreatePartidaCategoryDialog";
import Table, { buttonsStyle } from "../../../components/tables/table";

function getData(categories, expandedCategoryIds, searchText) {
	const byParent = new Map();
	const getCategoryKey = (id) => (id ? String(id).toLowerCase() : "root");
	const normalizedSearch = searchText.trim().toLowerCase();
	categories.forEach((category) => {
		const parentId = getCategoryKey(category.categoria_padre_id);
		byParent.set(parentId, [...(byParent.get(parentId) || []), category]);
	});

	const matchesSearch = (category) =>
		!normalizedSearch ||
		`${category.descripcion || ""} ${category.referencia || ""}`
			.toLowerCase()
			.includes(normalizedSearch);

	const hasMatchingDescendant = (category) =>
		(byParent.get(getCategoryKey(category.id)) || []).some(
			(child) => matchesSearch(child) || hasMatchingDescendant(child)
		);

	const result = [];
	const addBranch = (parentId, level, ancestorMatches) => {
		(byParent.get(getCategoryKey(parentId)) || [])
			.sort((a, b) => a.descripcion.localeCompare(b.descripcion))
			.forEach((category) => {
				const matches = matchesSearch(category);
				const descendantMatches = hasMatchingDescendant(category);
				const isVisible =
					!normalizedSearch || ancestorMatches || matches || descendantMatches;
				if (!isVisible) return;

				const hasChildren = byParent.has(getCategoryKey(category.id));
				result.push({
					id: category.id,
					descripcion: category.descripcion,
					referencia: category.referencia,
					nivel: level,
					tieneHijos: hasChildren,
					expandida: expandedCategoryIds.has(category.id),
				});
				const showChildren = normalizedSearch
					? ancestorMatches || matches || descendantMatches
					: expandedCategoryIds.has(category.id);
				if (showChildren)
					addBranch(category.id, level + 1, ancestorMatches || matches);
			});
	};

	addBranch("root", 0, false);
	return result;
}

export default function PartidasCategoriesPage() {
	const [data, setData] = useState([]);
	const [expandedCategoryIds, setExpandedCategoryIds] = useState(new Set());
	const [searchText, setSearchText] = useState("");
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [openCreateCategoryDialog, setOpenCreateCategoryDialog] = useState(false);
	const [categoryToEdit, setCategoryToEdit] = useState(null);

	const [openConfirmDialog, setOpenConfirmDialog] = useState(null);
	const [refresh, setRefresh] = useState(false);

	const loggedUser = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	function buttonFormatter(cell) {
		const elem = data.find((item) => item.id === cell);
		return (
			<>
				<Tooltip title="Editar">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={(e) => {
							e.stopPropagation();
								getPartidaCategoryById(cell, loggedUser?.accessToken)
									.then((res) => {
										if (res.status === 200) {
											setCategoryToEdit(res.data);
											setOpenCreateCategoryDialog(true);
										}
									})
									.catch((error) => {
										alertError({ error, customMessage: "No se ha podido cargar el capítulo para editarlo." });
									});
						}}
					>
						<EditIcon />
					</Button>
				</Tooltip>
				{loggedUser?.role?.rango === ROLES.SUPER_ADMIN && (
					<>
						<Tooltip title="Eliminar">
							<Button
								style={buttonsStyle}
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									setSelectedCategory(elem);
									setOpenConfirmDialog(2);
								}}
							>
								<DeleteIcon />
							</Button>
						</Tooltip>
					</>
				)}
			</>
		);
	}

	function toggleExpanded(categoryId) {
		setExpandedCategoryIds((current) => {
			const next = new Set(current);
			if (next.has(categoryId)) next.delete(categoryId);
			else next.add(categoryId);
			return next;
		});
	}

	function descriptionFormatter(cell, row) {

		return (
			<div
				className="d-flex align-items-center"
				style={{ paddingLeft: `${row.nivel * 32}px` }}
			>
				{row.tieneHijos ? (
					<Button
						size="small"
						onClick={(event) => {
							event.stopPropagation();
							toggleExpanded(row.id);
						}}
						style={{ minWidth: 28, width: 28, height: 28, marginRight: 8 }}
						aria-label={row.expandida ? "Ocultar subcategorías" : "Mostrar subcategorías"}
					>
						{row.expandida ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
					</Button>
				) : (
					<div style={{ width: 36 }} />
				)}
				<span style={{ fontWeight: row.tieneHijos ? 600 : 400 }}>
					{cell}
				</span>
			</div>
		);
	}

	const columns = [
		{
			dataField: "descripcion",
			text: "Descripción",
			sort: true,
			formatter: descriptionFormatter,
		},
		{
			dataField: "referencia",
			text: "Referencia",
			headerAlign: "center",
			align: "center",
		},
		{ dataField: "id", text: "", formatter: buttonFormatter },
	];

	useEffect(() => {
		getPartidaCategories(loggedUser?.accessToken)
			.then((res) => {
				if (res.status === 200) {
					setData(res.data);
					setRefresh(false);
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "No se han podido obtener las categorías.",
				});
			});
	}, [refresh]);

	return (
		<>
			<Card>
				<CardHeader title="Capítulos">
					<CardHeaderToolbar>
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => {
								setCategoryToEdit(null);
								setOpenCreateCategoryDialog(true);
							}}
						>
							Nuevo capítulo
						</button>
					</CardHeaderToolbar>
				</CardHeader>
				<CardBody>
					<TextField
						placeholder="Buscar"
						value={searchText}
						onChange={(event) => setSearchText(event.target.value)}
						variant="outlined"
						size="small"
					/>
					<Button
						className="ml-3"
						disabled={!searchText}
						onClick={() => setSearchText("")}
					>
						Clear
					</Button>
					<div className="mb-5" />
					<Table
						data={getData(data, expandedCategoryIds, searchText)}
						columns={columns}
						hideSearch
						rowEvents={{
							onClick: (e, row) => {
								if (row.tieneHijos) {
									toggleExpanded(row.id);
								}
							},
						}}
					/>
					<ConfirmDialog
						title={
							"¿Estás seguro de que quieres eliminar este capítulo?"
						}
						open={openConfirmDialog === 2}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							deletePartidaCategory(
								selectedCategory?.id,
								loggedUser?.accessToken
							)
								.then((res) => {
									if (
										res.status === 204 ||
										res.status === 200
									) {
										alertSuccess({
											title: "¡Eliminado!",
											customMessage:
												"Proveedor eliminado con éxito.",
										});
										setRefresh(true);
									}
								})
								.catch((error) => {
									alertError({
										error: error,
										customMessage:
											"No se ha podido eliminar el proveedor.",
									});
								});
						}}
					/>
					<CreatePartidaCategoryDialog
						open={openCreateCategoryDialog}
						setOpen={(nextOpen) => {
							setOpenCreateCategoryDialog(nextOpen);
							if (!nextOpen) setCategoryToEdit(null);
						}}
						categories={data}
						categoryToEdit={categoryToEdit}
						onCreated={(category) => {
							if (categoryToEdit) {
								setRefresh(true);
								return;
							}
							setData((current) => [...current, category]);
							if (category?.categoria_padre_id) {
								setExpandedCategoryIds((current) => {
									const next = new Set(current);
									next.add(category.categoria_padre_id);
									return next;
								});
							}
						}}
					/>
				</CardBody>
			</Card>
		</>
	);
}
